import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { taskService } from '../services/taskService';
import { TaskStatus, TaskStatusDefinition } from '../types/Task';
import { sortTaskStatuses } from '../utils/taskStatusTransitions';
import {
  buildStatusCreatePayload,
  buildStatusUpdatePayload,
  cloneStatusDraft,
  createEmptyNewStatusDraft,
  createSavedStatusDraftMap,
  EditableStatusDraft,
  getEffectiveStatusCode,
  mapDefinitionToStatusDraft,
  mapDraftToStatusDefinition,
  NewStatusDraft,
  normalizeStatusDraftOrders,
  serializeStatusDraft,
  STATUS_AUTOSAVE_DELAY_MS,
  StatusAutosaveResult,
  StatusDropPlacement,
  StatusValidationState,
} from '../utils/statusManager';

type UseStatusDraftManagerParams = {
  statusOptions: TaskStatusDefinition[];
  setStatusOptions: React.Dispatch<React.SetStateAction<TaskStatusDefinition[]>>;
  statusFilter: 'all' | TaskStatus;
  setStatusFilter: React.Dispatch<React.SetStateAction<'all' | TaskStatus>>;
  loadTasks: () => Promise<void>;
};

export const useStatusDraftManager = ({
  statusOptions,
  setStatusOptions,
  statusFilter,
  setStatusFilter,
  loadTasks,
}: UseStatusDraftManagerParams) => {
  const { t } = useTranslation(['tasks']);

  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusModalBusy, setStatusModalBusy] = useState(false);
  const [statusModalError, setStatusModalError] = useState<string | null>(null);
  const [statusDrafts, setStatusDrafts] = useState<EditableStatusDraft[]>([]);
  const [draggedStatusId, setDraggedStatusId] = useState<string | null>(null);
  const [dragOverStatusId, setDragOverStatusId] = useState<string | null>(null);
  const [dragOverPlacement, setDragOverPlacement] = useState<StatusDropPlacement>('below');
  const [pendingFocusedStatusId, setPendingFocusedStatusId] = useState<string | null>(null);
  const [statusAnnouncement, setStatusAnnouncement] = useState('');
  const [statusAnnouncementId, setStatusAnnouncementId] = useState(0);
  const [savingStatusIds, setSavingStatusIds] = useState<string[]>([]);
  const [statusAutosaveResults, setStatusAutosaveResults] = useState<Record<string, StatusAutosaveResult | undefined>>({});
  const [savedStatusDrafts, setSavedStatusDrafts] = useState<Record<string, EditableStatusDraft>>({});
  const [newStatusDraft, setNewStatusDraft] = useState<NewStatusDraft>(createEmptyNewStatusDraft(0));

  const statusAutosaveTimersRef = useRef<Record<string, number>>({});
  const statusSavedIndicatorTimersRef = useRef<Record<string, number>>({});
  const statusAnnouncementTimeoutRef = useRef<number | null>(null);
  const latestStatusDraftsRef = useRef<EditableStatusDraft[]>([]);
  const statusDragHandleRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  latestStatusDraftsRef.current = statusDrafts;

  const announceStatusReorder = useCallback((message: string) => {
    if (statusAnnouncementTimeoutRef.current) {
      window.clearTimeout(statusAnnouncementTimeoutRef.current);
    }

    setStatusAnnouncement('');
    setStatusAnnouncementId(current => current + 1);

    window.requestAnimationFrame(() => {
      setStatusAnnouncement(message);
      statusAnnouncementTimeoutRef.current = window.setTimeout(() => {
        setStatusAnnouncement('');
        statusAnnouncementTimeoutRef.current = null;
      }, 1200);
    });
  }, []);

  const getStatusReorderAnnouncement = useCallback((reorderedDrafts: EditableStatusDraft[], movedStatusId: string, previousIndex: number): string | null => {
    const movedIndex = reorderedDrafts.findIndex(draft => draft.id === movedStatusId);
    if (movedIndex < 0) {
      return null;
    }

    const movedStatusName = reorderedDrafts[movedIndex].name.trim() || t('statusLabel', { ns: 'tasks' });
    if (movedIndex === 0) {
      return t('statusReorderAnnouncementFirst', { ns: 'tasks', status: movedStatusName });
    }

    if (movedIndex === reorderedDrafts.length - 1) {
      return t('statusReorderAnnouncementLast', { ns: 'tasks', status: movedStatusName });
    }

    if (movedIndex < previousIndex) {
      const nextStatusName = reorderedDrafts[movedIndex + 1]?.name.trim() || t('statusLabel', { ns: 'tasks' });
      return t('statusReorderAnnouncementBefore', { ns: 'tasks', status: movedStatusName, target: nextStatusName });
    }

    const previousStatusName = reorderedDrafts[movedIndex - 1]?.name.trim() || t('statusLabel', { ns: 'tasks' });
    return t('statusReorderAnnouncementAfter', { ns: 'tasks', status: movedStatusName, target: previousStatusName });
  }, [t]);

  const syncStatusDrafts = useCallback((statuses: TaskStatusDefinition[]) => {
    const sorted = sortTaskStatuses(statuses);
    const mappedDrafts = sorted.map(mapDefinitionToStatusDraft);
    setStatusDrafts(mappedDrafts);
    setSavedStatusDrafts(createSavedStatusDraftMap(mappedDrafts));
    setStatusAutosaveResults({});
    setNewStatusDraft(createEmptyNewStatusDraft(sorted.length));
  }, []);

  const resetStatusDragState = useCallback(() => {
    setDraggedStatusId(null);
    setDragOverStatusId(null);
    setDragOverPlacement('below');
  }, []);

  const refreshStatuses = useCallback(async () => {
    const refreshed = await taskService.listTaskStatuses();
    const sorted = sortTaskStatuses(refreshed);
    setStatusOptions(sorted);
    syncStatusDrafts(sorted);
    if (statusFilter !== 'all' && !sorted.some(status => status.code === statusFilter)) {
      setStatusFilter('all');
    }
  }, [setStatusFilter, setStatusOptions, statusFilter, syncStatusDrafts]);

  const statusPreviewDefinitions = useMemo<TaskStatusDefinition[]>(() => {
    return sortTaskStatuses(statusDrafts.map(mapDraftToStatusDefinition));
  }, [statusDrafts]);

  const statusValidation = useMemo<StatusValidationState>(() => {
    const codeCounts = new Map<string, number>();
    const registerCode = (code: string) => {
      if (!code) return;
      codeCounts.set(code, (codeCounts.get(code) ?? 0) + 1);
    };

    statusDrafts.forEach(draft => registerCode(getEffectiveStatusCode(draft)));

    const newDraftEffectiveCode = getEffectiveStatusCode(newStatusDraft);
    const newDraftHasContent = Boolean(newStatusDraft.name.trim() || newStatusDraft.code.trim());
    if (newDraftHasContent) {
      registerCode(newDraftEffectiveCode);
    }

    const draftIssues = statusDrafts.reduce<Record<string, { duplicateCode: boolean; effectiveCode: string }>>((acc, draft) => {
      const effectiveCode = getEffectiveStatusCode(draft);
      acc[draft.id] = {
        duplicateCode: Boolean(effectiveCode) && (codeCounts.get(effectiveCode) ?? 0) > 1,
        effectiveCode,
      };
      return acc;
    }, {});

    const defaultCount = statusDrafts.filter(draft => draft.isDefault).length + (newDraftHasContent && newStatusDraft.isDefault ? 1 : 0);

    return {
      draftIssues,
      newDraft: {
        duplicateCode: Boolean(newDraftEffectiveCode) && (codeCounts.get(newDraftEffectiveCode) ?? 0) > 1,
        effectiveCode: newDraftEffectiveCode,
      },
      hasNoDefault: defaultCount === 0,
      hasMultipleDefaults: defaultCount > 1,
    };
  }, [newStatusDraft, statusDrafts]);

  const openStatusModal = useCallback(() => {
    syncStatusDrafts(statusOptions);
    setStatusModalError(null);
    resetStatusDragState();
    setIsStatusModalOpen(true);
  }, [resetStatusDragState, statusOptions, syncStatusDrafts]);

  const closeStatusModal = useCallback(() => {
    if (statusModalBusy || savingStatusIds.length > 0) return;
    setIsStatusModalOpen(false);
    setStatusModalError(null);
    resetStatusDragState();
  }, [resetStatusDragState, savingStatusIds.length, statusModalBusy]);

  const clearStatusAutosaveResult = useCallback((statusId: string) => {
    if (statusSavedIndicatorTimersRef.current[statusId]) {
      window.clearTimeout(statusSavedIndicatorTimersRef.current[statusId]);
      delete statusSavedIndicatorTimersRef.current[statusId];
    }

    setStatusAutosaveResults(prev => {
      if (!prev[statusId]) {
        return prev;
      }

      return { ...prev, [statusId]: undefined };
    });
  }, []);

  const updateStatusDraft = useCallback((statusId: string, patch: Partial<EditableStatusDraft>) => {
    clearStatusAutosaveResult(statusId);
    setStatusDrafts(prev => prev.map(status => status.id === statusId ? { ...status, ...patch } : status));
  }, [clearStatusAutosaveResult]);

  const setStatusDraftDefault = useCallback((statusId: string, checked: boolean) => {
    clearStatusAutosaveResult(statusId);
    setStatusDrafts(prev => prev.map(status => {
      if (checked) {
        clearStatusAutosaveResult(status.id);
      }

      if (status.id === statusId) {
        return { ...status, isDefault: checked };
      }

      return checked ? { ...status, isDefault: false } : status;
    }));

    if (checked) {
      setNewStatusDraft(prev => ({ ...prev, isDefault: false }));
    }
  }, [clearStatusAutosaveResult]);

  const setNewStatusDraftDefault = useCallback((checked: boolean) => {
    setNewStatusDraft(prev => ({ ...prev, isDefault: checked }));

    if (checked) {
      statusDrafts.forEach(status => clearStatusAutosaveResult(status.id));
      setStatusDrafts(prev => prev.map(status => ({ ...status, isDefault: false })));
    }
  }, [clearStatusAutosaveResult, statusDrafts]);

  const isStatusDraftDirty = useCallback((draft: EditableStatusDraft): boolean => {
    const savedDraft = savedStatusDrafts[draft.id];
    if (!savedDraft) {
      return false;
    }

    return serializeStatusDraft(draft) !== serializeStatusDraft(savedDraft);
  }, [savedStatusDrafts]);

  const markStatusAutosaveSaved = useCallback((statusId: string) => {
    if (statusSavedIndicatorTimersRef.current[statusId]) {
      window.clearTimeout(statusSavedIndicatorTimersRef.current[statusId]);
    }

    setStatusAutosaveResults(prev => ({ ...prev, [statusId]: 'saved' }));
    statusSavedIndicatorTimersRef.current[statusId] = window.setTimeout(() => {
      setStatusAutosaveResults(prev => ({ ...prev, [statusId]: undefined }));
      delete statusSavedIndicatorTimersRef.current[statusId];
    }, 1600);
  }, []);

  const persistStatusDraft = useCallback(async (statusId: string) => {
    const draft = latestStatusDraftsRef.current.find(item => item.id === statusId);
    if (!draft) {
      return;
    }

    const name = draft.name.trim();
    if (!name || statusValidation.draftIssues[statusId]?.duplicateCode) {
      return;
    }

    setSavingStatusIds(prev => prev.includes(statusId) ? prev : [...prev, statusId]);
    setStatusModalError(null);

    try {
      const defaultStatusesToUnset = draft.isDefault
        ? latestStatusDraftsRef.current.filter(status => status.id !== statusId && status.isDefault)
        : [];

      await Promise.all([
        taskService.updateTaskStatus(draft.id, buildStatusUpdatePayload(draft)),
        ...defaultStatusesToUnset.map(status => taskService.updateTaskStatus(status.id, { isDefault: false })),
      ]);

      setSavedStatusDrafts(prev => {
        const next = { ...prev, [draft.id]: cloneStatusDraft({ ...draft, name, code: draft.code.trim() }) };
        defaultStatusesToUnset.forEach(status => {
          const existing = prev[status.id] ?? status;
          next[status.id] = cloneStatusDraft({ ...existing, isDefault: false });
        });
        return next;
      });

      markStatusAutosaveSaved(statusId);
      void loadTasks();
    } catch (requestError) {
      setStatusAutosaveResults(prev => ({ ...prev, [statusId]: 'error' }));
      setStatusModalError(requestError instanceof Error ? requestError.message : t('failedToSave', { ns: 'tasks' }));
    } finally {
      setSavingStatusIds(prev => prev.filter(id => id !== statusId));
    }
  }, [loadTasks, markStatusAutosaveSaved, statusValidation.draftIssues, t]);

  const handleCreateStatus = useCallback(async () => {
    const name = newStatusDraft.name.trim();
    if (!name) {
      setStatusModalError(t('statusNameRequired', { ns: 'tasks' }));
      return;
    }

    if (statusValidation.newDraft.duplicateCode) {
      setStatusModalError(t('statusCodeDuplicate', { ns: 'tasks' }));
      return;
    }

    setStatusModalBusy(true);
    setStatusModalError(null);
    try {
      await taskService.createTaskStatus(buildStatusCreatePayload(newStatusDraft));
      if (newStatusDraft.isDefault) {
        await Promise.all(statusDrafts
          .filter(draft => draft.isDefault)
          .map(draft => taskService.updateTaskStatus(draft.id, { isDefault: false })));
      }
      await refreshStatuses();
      void loadTasks();
    } catch (requestError) {
      setStatusModalError(requestError instanceof Error ? requestError.message : t('failedToSave', { ns: 'tasks' }));
    } finally {
      setStatusModalBusy(false);
    }
  }, [loadTasks, newStatusDraft, refreshStatuses, statusDrafts, statusValidation.newDraft.duplicateCode, t]);

  const handleDeleteStatus = useCallback(async (draft: EditableStatusDraft) => {
    if (!window.confirm(t('deleteStatusConfirm', { ns: 'tasks', status: draft.name }))) return;

    setStatusModalBusy(true);
    setStatusModalError(null);
    try {
      await taskService.deleteTaskStatus(draft.id);
      await refreshStatuses();
      void loadTasks();
    } catch (requestError) {
      setStatusModalError(requestError instanceof Error ? requestError.message : t('failedToDelete', { ns: 'tasks' }));
    } finally {
      setStatusModalBusy(false);
    }
  }, [loadTasks, refreshStatuses, t]);

  const handleReorderStatus = useCallback(async (statusId: string, targetIndex: number, restoreFocus = false) => {
    if (statusModalBusy || savingStatusIds.length > 0) return;

    const currentIndex = statusDrafts.findIndex(status => status.id === statusId);
    if (currentIndex < 0) return;
    if (targetIndex < 0 || targetIndex > statusDrafts.length) return;
    if (targetIndex === currentIndex || targetIndex === currentIndex + 1) return;

    const previousDrafts = statusDrafts;
    const reorderedDrafts = [...statusDrafts];
    const [movedDraft] = reorderedDrafts.splice(currentIndex, 1);
    const insertionIndex = targetIndex > currentIndex ? targetIndex - 1 : targetIndex;
    reorderedDrafts.splice(insertionIndex, 0, movedDraft);

    const normalizedDrafts = normalizeStatusDraftOrders(reorderedDrafts);
    const reorderAnnouncement = getStatusReorderAnnouncement(normalizedDrafts, statusId, currentIndex);
    const changedDrafts = normalizedDrafts.filter((draft, index) => {
      const previousDraft = previousDrafts[index];
      return previousDraft && previousDraft.id !== draft.id;
    });

    if (restoreFocus) {
      setPendingFocusedStatusId(statusId);
    }

    setStatusDrafts(normalizedDrafts);
    setStatusModalBusy(true);
    setStatusModalError(null);
    try {
      await Promise.all(changedDrafts.map(draft => taskService.updateTaskStatus(draft.id, buildStatusUpdatePayload(draft))));
      if (reorderAnnouncement) {
        announceStatusReorder(reorderAnnouncement);
      }
      await refreshStatuses();
      void loadTasks();
    } catch (requestError) {
      setStatusDrafts(previousDrafts);
      setStatusModalError(requestError instanceof Error ? requestError.message : t('failedToSave', { ns: 'tasks' }));
    } finally {
      setStatusModalBusy(false);
      resetStatusDragState();
    }
  }, [announceStatusReorder, getStatusReorderAnnouncement, loadTasks, refreshStatuses, resetStatusDragState, savingStatusIds.length, statusDrafts, statusModalBusy, t]);

  useEffect(() => {
    if (!isStatusModalOpen || statusModalBusy) {
      return;
    }

    const activeDraftIds = new Set(statusDrafts.map(draft => draft.id));

    Object.keys(statusAutosaveTimersRef.current).forEach(statusId => {
      if (!activeDraftIds.has(statusId)) {
        window.clearTimeout(statusAutosaveTimersRef.current[statusId]);
        delete statusAutosaveTimersRef.current[statusId];
      }
    });

    statusDrafts.forEach(draft => {
      const isDirty = isStatusDraftDirty(draft);
      const isValid = Boolean(draft.name.trim()) && !statusValidation.draftIssues[draft.id]?.duplicateCode;
      const isSaving = savingStatusIds.includes(draft.id);

      if (isDirty && isValid && !isSaving) {
        if (!statusAutosaveTimersRef.current[draft.id]) {
          statusAutosaveTimersRef.current[draft.id] = window.setTimeout(() => {
            delete statusAutosaveTimersRef.current[draft.id];
            void persistStatusDraft(draft.id);
          }, STATUS_AUTOSAVE_DELAY_MS);
        }
        return;
      }

      if (statusAutosaveTimersRef.current[draft.id]) {
        window.clearTimeout(statusAutosaveTimersRef.current[draft.id]);
        delete statusAutosaveTimersRef.current[draft.id];
      }
    });
  }, [isStatusDraftDirty, isStatusModalOpen, persistStatusDraft, savingStatusIds, statusDrafts, statusModalBusy, statusValidation.draftIssues]);

  useEffect(() => () => {
    Object.values(statusAutosaveTimersRef.current).forEach(timerId => window.clearTimeout(timerId));
    Object.values(statusSavedIndicatorTimersRef.current).forEach(timerId => window.clearTimeout(timerId));
    if (statusAnnouncementTimeoutRef.current) {
      window.clearTimeout(statusAnnouncementTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (!pendingFocusedStatusId || statusModalBusy || savingStatusIds.length > 0 || !isStatusModalOpen) {
      return;
    }

    const target = statusDragHandleRefs.current[pendingFocusedStatusId];
    if (!target) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      target.focus();
      setPendingFocusedStatusId(null);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isStatusModalOpen, pendingFocusedStatusId, savingStatusIds.length, statusModalBusy, statusDrafts]);

  return {
    isStatusModalOpen,
    statusModalBusy,
    statusModalError,
    statusDrafts,
    draggedStatusId,
    dragOverStatusId,
    dragOverPlacement,
    statusAnnouncement,
    statusAnnouncementId,
    savingStatusIds,
    statusAutosaveResults,
    newStatusDraft,
    statusPreviewDefinitions,
    statusValidation,
    statusDragHandleRefs,
    setNewStatusDraft,
    setNewStatusDraftDefault,
    setDraggedStatusId,
    setDragOverStatusId,
    setDragOverPlacement,
    openStatusModal,
    closeStatusModal,
    updateStatusDraft,
    setStatusDraftDefault,
    isStatusDraftDirty,
    handleCreateStatus,
    handleDeleteStatus,
    handleReorderStatus,
  };
};